import React, { lazy, Suspense, useState } from 'react'
import { useS4LContext } from '@s4l/react-context'
import { useDispatch } from 'react-redux'
import { useDevice } from 'src/hooks/useDevice'

import { Button, Collapsible } from '@s4l/ds'
import Input from 'src/comps/Atom/Inputs/Input/Input'
import Spinner from 'client/icons/Spinner'

import { displayAlertAction } from 'src/comps/AlertMessage/actions'
import { fetchVoucherRequestAction } from 'src/comps/Voucher/actions'
import { BreakpointSizes } from 'src/types/device'
import { type HolderInput } from '@s4l/stayforlong/booking/Holder'

import styles from '../styles.module.css'

const CountryResidenceSelector = lazy(() => import('client/components/CountryResidenceSelector'))

interface CountryValue {
  code: string
  name: string
  originalName: string
}

interface GuestData {
  id: string
  firstName: string
  lastName: string
  country: CountryValue
}

interface GuestTouched {
  firstName: boolean
  lastName: boolean
  country: boolean
}

interface IVoucherGuestRoomListFormProps {
  alertContainerId: string
  hash: string
  initialGuest: Omit<GuestData, 'country'>
  isMultiBooking: boolean
  onClose: () => void
  pendingRoomingListCount: number
  purchaseId: string
  totalGuests: number
}

const VoucherGuestRoomListForm = ({
  alertContainerId,
  hash,
  initialGuest,
  isMultiBooking,
  onClose,
  pendingRoomingListCount,
  purchaseId,
  totalGuests,
}: IVoucherGuestRoomListFormProps) => {
  const { domain, i18n } = useS4LContext()
  const dispatch = useDispatch()
  const { activeBreakpoint } = useDevice()
  const isDesktop = activeBreakpoint === BreakpointSizes.L

  const initialTouchedFields = Array.from({ length: totalGuests }, () => ({
    firstName: false,
    lastName: false,
    country: false,
  }))

  const initialGuests = Array.from({ length: totalGuests }, (_, index) => ({
    id: String(index),
    firstName: index === 0 ? initialGuest.firstName : '',
    lastName: index === 0 ? initialGuest.lastName : '',
    country: { code: '', name: '', originalName: '' },
  }))

  const initialOpenStates = Array.from({ length: totalGuests }, (_, index) => index === 0)

  const [guests, setGuests] = useState<GuestData[]>(initialGuests)
  const [touchedFields, setTouchedFields] = useState<GuestTouched[]>(initialTouchedFields)
  const [openStates, setOpenStates] = useState<boolean[]>(initialOpenStates)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const hasErrors = guests.some(guest => !guest.firstName || !guest.lastName || !guest.country.code)

  const updateGuestField = (
    index: number,
    field: 'firstName' | 'lastName' | 'country',
    value: string | CountryValue
  ) => {
    setGuests(prevGuests => {
      const next = [...prevGuests]
      if (field === 'country') {
        next[index] = { ...next[index], country: value as CountryValue }
      } else {
        next[index] = { ...next[index], [field]: value as string }
      }
      return next
    })
  }

  const markFieldAsTouched = (index: number, field: keyof GuestTouched) => {
    setTouchedFields(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: true }
      return next
    })
  }

  const dispatchAlert = ({
    title,
    description,
    severity,
    containerId,
  }: {
    title: string
    description?: string
    severity?: 'error' | 'success'
    containerId?: string
  }) =>
    dispatch(
      displayAlertAction({
        title,
        description,
        severity,
        containerId,
      })
    )

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitted(true)

    if (hasErrors) {
      const errorGuests = guests.map(
        ({ firstName, lastName, country }) => !firstName || !lastName || !country.code
      )
      setOpenStates(prev => prev.map((open, index) => (errorGuests[index] ? true : open)))
      dispatchAlert({
        title: i18n.formatMessage({ id: 'guest-rooming-list.error-notification-title' }),
        description: i18n.formatMessage({ id: 'guest-rooming-list.error-notification-body' }),
        containerId: alertContainerId,
      })
      return
    }

    const holders = guests.map(guest => ({
      firstName: guest.firstName,
      lastName: guest.lastName,
      country: guest.country.originalName,
    }))

    const [error] = await domain.CreateGuestRoomingListBookingUseCase.execute({
      hash,
      holders: holders as unknown as HolderInput[],
    })

    if (error) {
      dispatchAlert({
        title: i18n.formatMessage({ id: 'error.pre-title' }),
        description: i18n.formatMessage({ id: 'error.request-try-later' }),
        containerId: alertContainerId,
      })
      console.error(error)
      return
    }

    const successMessageId =
      isMultiBooking && pendingRoomingListCount > 1
        ? 'guest-rooming-list.success-notification-body-multibooking'
        : 'guest-rooming-list.success-notification-body'

    dispatchAlert({
      title: i18n.formatMessage({ id: 'guest-rooming-list.success-notification-title' }),
      description: i18n.formatMessage({ id: successMessageId }),
      severity: 'success',
    })
    dispatch(fetchVoucherRequestAction({ purchaseId }))
    onClose()
  }

  const buildRequiredErrors = (shouldShow: boolean): string[] =>
    shouldShow ? [i18n.formatMessage({ id: 'guest-rooming-list.input-inline-error' })] : []

  return (
    <form onSubmit={handleFormSubmit} className={styles.form}>
      {guests.map((guest, index) => (
        <Collapsible
          key={guest.id}
          title={i18n.formatMessage(
            { id: 'guest-rooming-list.guest-number' },
            { number: index + 1 }
          )}
          open={openStates[index]}
          onToggle={next =>
            setOpenStates(prev => {
              const updated = [...prev]
              updated[index] = next
              return updated
            })
          }
        >
          {openStates[index] && (
            <div className={styles.collapsibleContent}>
              <div>
                <Input
                  label={i18n.formatMessage({ id: 'user.first-name' })}
                  name={`guest-name-${index}`}
                  value={guest.firstName}
                  onChange={value => updateGuestField(index, 'firstName', value)}
                  onBlur={() => markFieldAsTouched(index, 'firstName')}
                  errors={buildRequiredErrors(
                    (isSubmitted || touchedFields[index]?.firstName) && !guest.firstName
                  )}
                />
              </div>

              <div>
                <Input
                  label={i18n.formatMessage({ id: 'user.last-name' })}
                  name={`guest-surname-${index}`}
                  value={guest.lastName}
                  onChange={value => updateGuestField(index, 'lastName', value)}
                  onBlur={() => markFieldAsTouched(index, 'lastName')}
                  errors={buildRequiredErrors(
                    (isSubmitted || touchedFields[index]?.lastName) && !guest.lastName
                  )}
                />
              </div>

              <Suspense fallback={<Spinner />}>
                <CountryResidenceSelector
                  onSelect={country => updateGuestField(index, 'country', country)}
                  value={guest.country}
                  errors={buildRequiredErrors(
                    (isSubmitted || touchedFields[index]?.country) && !guest.country.code
                  )}
                />
              </Suspense>
            </div>
          )}
        </Collapsible>
      ))}

      <div className={styles.ctaContainer}>
        <Button type="submit" variant="filled-rounded" fullWidth={!isDesktop}>
          {i18n.formatMessage({ id: 'guest-rooming-list.cta' })}
        </Button>
      </div>
    </form>
  )
}

export default VoucherGuestRoomListForm
